import {
  convertGrossProfitToPriceMicros,
  convertPriceMicrosToGrossProfit,
} from '@/utils/money';
import { isValidNumber } from '@/utils/number';

export const PricingCalculations = {
  /**
   * Calculate gross margin percentage from gross profit amount and price.
   * Formula: GM% = GP$ / Price
   *
   * @param grossProfitInMicros - The gross profit amount in micros
   * @param priceInMicros - The selling price in micros
   * @returns Gross margin as a percentage (e.g., 20 for 20%), or null if invalid
   */

  grossMarginPercentFromGrossProfit(
    grossProfitInMicros: number | null | undefined,
    priceInMicros: number | null | undefined,
  ): number | null {
    if (!isValidNumber(grossProfitInMicros) || !isValidNumber(priceInMicros)) {
      return null;
    }
    if (priceInMicros === 0) {
      return null;
    }

    return (grossProfitInMicros / priceInMicros) * 100;
  },

  /**
   * Calculate gross profit amount from price and cost.
   * Formula: GP$ = Price - Cost
   *
   * @param priceInMicros - The selling price in micros
   * @param costInMicros - The cost in micros
   * @returns Gross profit amount in micros, or null if invalid
   */
  grossProfitAmountFromPrice(
    priceInMicros: number | null | undefined,
    costInMicros: number | null | undefined,
  ): number | null {
    if (!isValidNumber(priceInMicros) || !isValidNumber(costInMicros)) {
      return null;
    }
    return priceInMicros - costInMicros;
  },
  /**
   * Calculate gross profit percentage from price and cost.
   * Formula: GP% = (1 - Cost/Price) * 100
   *
   * @param priceInMicros - The selling price in micros
   * @param costInMicros - The cost in micros
   * @returns Gross profit as a percentage (e.g., 20 for 20%), or null if invalid
   */
  grossProfitPercentFromPrice(
    priceInMicros: number | null | undefined,
    costInMicros: number | null | undefined,
  ): number | null {
    if (!isValidNumber(priceInMicros) || !isValidNumber(costInMicros)) {
      return null;
    }
    if (priceInMicros === 0) {
      return null;
    }
    return convertPriceMicrosToGrossProfit(costInMicros, priceInMicros);
  },

  /**
   * Calculate price from gross profit amount and cost.
   * Formula: Price = Cost + GP$
   *
   * @param grossProfitInMicros - The gross profit amount in micros
   * @param costInMicros - The cost in micros
   * @returns Price in micros, or null if invalid
   */
  priceFromGrossProfitAmount(
    grossProfitInMicros: number | null | undefined,
    costInMicros: number | null | undefined,
  ): number | null {
    if (!isValidNumber(grossProfitInMicros) || !isValidNumber(costInMicros)) {
      return null;
    }
    return costInMicros + grossProfitInMicros;
  },

  /**
   * Calculate price from gross profit percentage and cost.
   * Formula: Price = Cost / (1 - GP%/100)
   *
   * @param grossProfitPercent - Target gross profit percentage (e.g., 20 for 20%)
   * @param costInMicros - The cost in micros
   * @returns Price in micros, or null if GP% >= 100 or invalid inputs
   */
  priceFromGrossProfitPercent(
    grossProfitPercent: number | null | undefined,
    costInMicros: number | null | undefined,
  ): number | null {
    if (!isValidNumber(grossProfitPercent) || !isValidNumber(costInMicros)) {
      return null;
    }
    if (grossProfitPercent >= 100) {
      return null; // Cannot have 100%+ gross profit (division by zero)
    }
    return convertGrossProfitToPriceMicros(costInMicros, grossProfitPercent);
  },
};
